import { notify } from '../services/notification/notificationBus'
import { isObject, isEmpty, isSet, isString } from '@/global/utils/utils'

const tableMixin = {
  data () {
    return {
      exportLoader: false
    }
  },

  methods: {
    export (config, exportFileName, fetchParams = {}) {
      try {
        const { apiConfig, query } = config || {}

        // do not export if api config is invalid
        if (!this.isExportApiConfigValid(apiConfig)) {
          notify(this.$t('base/export.invalid_api_config'))
          return
        }
        // do not export if file name is not valid
        if (!exportFileName) {
          notify(this.$t('base/export.invalid_export_file_name'))
          return
        }

        const { module, route } = apiConfig
        const { filters, sort, columns } = query || {}
        const url = new URL(`${window.location.origin}/api/${module}/${route}`)
        let objectURL = null
        this.exportLoader = true
        const params = new URLSearchParams()

        // *** add query to the api endpoint ***
        // add filters
        if (filters && isObject(filters)) {
          params.append('filters', JSON.stringify(filters))
        }
        // add sort
        if (sort && isObject(sort)) {
          params.append('sort', JSON.stringify(sort))
        }
        // add columns
        if (columns?.length) {
          for (const column of columns) {
            params.append('columns[]', column)
          }
        }

        // call api
        fetch(`${url.toString()}?${params.toString()}`, fetchParams)
          .then(response => {
            if (!response.ok) {
              throw new Error(response.statusText)
            }
            return response.arrayBuffer()
          })
          .then(arrayBuffer => {
            // BE endpoint sends a readable stream of bytes
            const byteArray = new Uint8Array(arrayBuffer)
            const link = document.createElement('a')
            objectURL = URL.createObjectURL(
              new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
            )
            link.href = objectURL
            link.download = exportFileName
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          })
          .catch(exception => {
            console.error(exception)
            notify(this.$t('base.export_not_possible'))
          })
          .finally(() => {
            this.exportLoader = false
            if (objectURL) {
              URL.revokeObjectURL(objectURL)
            }
          })
      }
      catch (exception) {
        console.error(exception)
        notify(exception?.response?.data?.message || exception)
      }
    },

    isExportApiConfigValid (config) {
      const { module, route } = config || {}
      return module && route
    },

    /**
     * Navigates to table view (for example from table details view).
     *
     * @param {string} viewName - View name to which we want to go (its defined in wanted view routes)
     */
    navigatesToTableView (viewName) {
      if (!isSet(viewName) || !isString(viewName)) {
        return
      }

      this.$router.push({
        name: viewName
      }).then()
    },

    /**
     * Navigates to a table details view.
     *
     * @param {string} viewName - The name of the target view (must match a defined route name).
     * @param {Object} event - Object containing row-related data.
     * @param {number} event.id - Identifier of the selected table row.
     * @param {Object} [query={}] - Query parameters to merge with or override default query params.
     * @param {Object} [params={}] - Route params to merge with or override default params.
     */
    navigatesToTableDetailsView (viewName, event, query = {}, params = {}) {
      const { data } = event || {}
      const { id } = data || {}
      let paramsValue = {}
      let queryValue = {}

      // check if method can be executed
      // viewName is mandatory and should be a plain string
      // id is mandatory, so we can know which data to show in details view
      if (!isSet(viewName) || !isString(viewName) || !id) {
        return
      }

      // set id to params
      paramsValue = { id }

      // merge query params
      if (isSet(query) && !isEmpty(query)) {
        queryValue = {
          ...queryValue,
          ...query
        }
      }
      // merge params
      if (isSet(params) && !isEmpty(params)) {
        paramsValue = {
          ...paramsValue,
          ...params
        }
      }

      this.$router.push({
        name: viewName,
        params: paramsValue,
        query: queryValue
      }).then()
    }
  }
}

export default tableMixin
